// The palette behind every generated thumbnail and feature card.
//
// It used to be three random hues: `base = rnd() * 360`, a neighbour 26–70°
// away, and a complement for netti. Random hues off the raw wheel are exactly
// what the organism's own minting was rebuilt to STOP doing — they land on the
// muddy olives and dead teals that make a card look like a bug report, and two
// posts could come out nearly the same colour because nothing kept them apart.
//
// So a thumbnail now wears the ORGANISM's colour intelligence. `organismFromSeed`
// puts the seed on one of sixteen curated, hand-tuned palettes (a hue, a harmony
// rule, and a saturation/lightness/contrast inside the known-good band) and
// jitters it per-seed; `derivePalette` expands that into the six real colours a
// netti is actually made of — core, body, outer, fringe, halo and the luminous
// inner glow. The card is painted from those. It is the same creature's colours,
// so a card reads as innernet rather than as a random gradient, it can never be
// muddy, and it is far more varied than three numbers off a wheel — because the
// variation is a whole harmony, not one hue.
//
// Pure and dependency-light on purpose: the CSS thumbnail (client) and the
// Satori feature card (server, no DOM) both render from this one source.

import {
  derivePalette,
  hslToRgb,
  rgbToCss,
  representativeOrganismHue,
  type ColorState,
  type HSL,
  type RGB,
} from '../organism/color';
import { organismFromHash, seedFromString } from '../organism/identity';

/** a seeded 0..1 stream (mulberry32) — stable per seed, for the non-colour choices */
function rngFrom(seed: number): () => number {
  let a = seed || 1;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

const shade = (c: RGB, t: number): RGB => mix(c, [0, 0, 0], t);

/**
 * Push a colour away from its own grey, keeping roughly the same luminance.
 *
 * Shading toward black takes CHROMA with it, not just lightness — which is why
 * a deep card ground came out muddy: a vivid coral core, shaded far enough for
 * white type to clear contrast, arrives as brown. Re-spreading the channels
 * around their mean puts the colour back without lifting the ground toward the
 * ink, so the card reads as a deep version of the organism's own hue rather
 * than as dirt.
 */
const saturate = (c: RGB, k: number): RGB => {
  const m = (c[0] + c[1] + c[2]) / 3;
  return c.map((v) => Math.max(0, Math.min(1, m + (v - m) * k))) as RGB;
};
const tint = (c: RGB, t: number): RGB => mix(c, [1, 1, 1], t);

/** rgba() css from an RGB (0..1) + alpha. Satori understands rgba, not color-mix. */
function rgba(c: RGB, a: number): string {
  const [r, g, b] = c.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export interface ThumbPalette {
  /** the organism state the seed landed on — the same one its netti wears */
  state: ColorState;
  /** the representative hue, for anything that still wants a single number */
  hue: number;
  /**
   * THE ground — one solid, deep tone from the organism's core. The sweep
   * (groundFrom → groundTo) was retired 2026-08-09: a gradient behind type
   * means the ink's contrast is different at the top of the card than at the
   * bottom, and the cards read busier than the product they picture. One tone,
   * checked against the ink below, everywhere the thumbnail family renders.
   */
  ground: string;
  /** legacy sweep endpoints — both now carry `ground`, so every CSS gradient
   *  that still references them renders flat without a markup change. */
  groundFrom: string;
  groundTo: string;
  /** the sweep's angle (deg) — varied per seed so cards don't all lean the same */
  angle: number;
  /** the luminous bloom behind netti, from its own inner-glow hue */
  glow: string;
  glowSoft: string;
  /** the fringe wash in the opposite corner — the third hue, kept subtle */
  fringe: string;
  /** where the bloom sits, as viewport-percent — moves per seed */
  glowAt: { x: number; y: number };
  /** ink that always reads on this ground */
  ink: string;
  inkSoft: string;
  /**
   * The one coloured mark on the card — the wordmark's dot and leaf. It is the
   * creature's own hue, lifted until it reads on a deep ground: on a coloured
   * card the brand blue is the only thing in the picture that came from
   * somewhere else, and inking it the same white as the wordmark just erases it.
   */
  accent: string;
  /** a hair of turn on netti, both directions */
  tilt: number;
  /**
   * The bloom to plant in this card — the real rendered organism, baked onto
   * this seed's own curated hue (scripts/bake-netti-hues.mjs).
   *
   * Without this every generated card carried the identical green default
   * bloom, so sixteen carefully-chosen grounds all wore one creature and the
   * palette underneath was invisible. A per-user baked bloom, where one exists,
   * still wins over this — that is the person's real netti (law_one_netti).
   */
  nettiSrc: string;
  /**
   * The hue that bloom is MEANT to be at. `nettiSrc` is the nearest one stocked
   * on disk; a server that can rasterise (lib/brand/bloom) rotates the source to
   * exactly this instead, which is what a card wears now that its creature has
   * to be the same creature standing on the page.
   */
  nettiHue: number;
}

/** the sixteen curated mint hues — MUST match MINT_PALETTES in lib/organism/identity.ts */
const BAKED_HUES = [104, 158, 188, 202, 224, 250, 274, 300, 328, 344, 12, 28, 42, 64, 84, 134];

/** the baked bloom nearest a hue, going the short way round the wheel */
export function nettiForHue(hue: number): string {
  let best = BAKED_HUES[0];
  let bestD = 360;
  for (const h of BAKED_HUES) {
    const d = Math.abs(((h - hue + 540) % 360) - 180);
    if (d < bestD) {
      bestD = d;
      best = h;
    }
  }
  return `/brand/netti/hue-${best}.png`;
}

/**
 * The palette for a seed. `seed` is whatever identifies the thing being
 * pictured — a post slug, a feature id.
 */
/** WCAG relative luminance of an RGB in 0..1 (sRGB linearized). */
function luminance(c: RGB): number {
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
}

/** WCAG contrast ratio between two colours. */
function contrastRatio(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** the two inks a card may wear — the paper white and the letter's own dark */
const INK_LIGHT: RGB = [255 / 255, 252 / 255, 246 / 255];
const INK_DARK: RGB = [43 / 255, 38 / 255, 34 / 255];

export function thumbPalette(seed: string): ThumbPalette {
  const h = seedFromString(seed);
  const rnd = rngFrom(h);
  const state = organismFromHash(h);
  const p = derivePalette(state);

  // ONE solid ground: the organism's core, shaded until the ink below clears
  // WCAG AA for large type (3:1) with room to spare. Starting depth keeps the
  // colour saturated; the loop only darkens further when a bright core would
  // otherwise leave white ink swimming.
  let ground = saturate(shade(p.core, 0.38), 1.3);
  let ink = contrastRatio(INK_LIGHT, ground) >= contrastRatio(INK_DARK, ground) ? INK_LIGHT : INK_DARK;
  for (let i = 0; i < 5 && contrastRatio(ink, ground) < 4.5; i++) {
    // every darkening step re-spreads the chroma it just cost us
    ground = saturate(shade(ground, 0.14), 1.22);
    ink = contrastRatio(INK_LIGHT, ground) >= contrastRatio(INK_DARK, ground) ? INK_LIGHT : INK_DARK;
  }
  const groundCss = rgbToCss(ground);

  // Vary the geometry too, not only the colour — two posts on the same curated
  // palette still arrive looking like different pictures.
  const hue = representativeOrganismHue(state);
  const angle = Math.round(118 + rnd() * 74); // kept for callers that still lean
  const glowAt = { x: Math.round(58 + rnd() * 22), y: Math.round(32 + rnd() * 26) };
  // netti takes whichever of the harmony's own hues stands FURTHEST from the
  // ground. Taking the secondary unconditionally was already better than taking
  // the ground's own hue (a mint bloom on a green sweep), but on the palettes
  // whose secondary sits close to the primary the creature still sank into the
  // card. Every candidate here is a hue the palette was designed around, so
  // picking the far one contrasts hard without ever clashing.
  const away = (a: number, b: number) => Math.abs(((a - b + 540) % 360) - 180);
  const nettiHue = [state.secondary.hue, state.tinge?.hue, (state.primary.hue + 180) % 360]
    .filter((x): x is number => typeof x === 'number')
    .reduce((best, cand) => (away(cand, hue) > away(best, hue) ? cand : best), state.secondary.hue);

  const inkCss = ink === INK_LIGHT ? 'rgba(255, 252, 246, 0.97)' : 'rgba(43, 38, 34, 0.94)';

  return {
    state,
    hue,
    nettiSrc: nettiForHue(nettiHue),
    nettiHue,
    ground: groundCss,
    groundFrom: groundCss,
    groundTo: groundCss,
    angle,
    glow: rgba(tint(p.glow, 0.18), 0.58),
    glowSoft: rgba(tint(p.halo, 0.3), 0.34),
    fringe: rgba(tint(p.fringe, 0.12), 0.3),
    glowAt,
    ink: inkCss,
    inkSoft: ink === INK_LIGHT ? rgba(tint(p.halo, 0.55), 0.82) : 'rgba(43, 38, 34, 0.6)',
    accent: rgbToCss(hslToRgb({ h: nettiHue, s: 0.86, l: 0.68 })),
    tilt: Math.round(rnd() * 22 - 11),
  };
}

// ── the portal palette ──────────────────────────────────────────────────────
// A card built from a PAGE rather than from a hash.
//
// `thumbPalette` above answers "what colour should this thing be?" — it is the
// right question for a feature card or an email banner, which picture something
// that has no page of its own. It is the wrong question for a post, a
// comparison, or a room: those DO have a page, the page already wears a colour,
// and a picture that invents a second one is a door painted a different colour
// from the room behind it. You clicked a green card and arrived in a lavender
// room (law_portal).
//
// So this takes the page's own ground and the page's own creature, and only
// changes the VALUE: the same hue, deepened until light ink sits on it, because
// a card is read at thumbnail size in a feed and in a chat unfurl where the
// page's pale ground would dissolve. Hue and creature are the page's, exactly;
// depth is the card's own job.

/** `hsl(288 60% 88%)` / `hsl(288, 60%, 88%)` → { h, s, l } with s,l in 0..1. */
export function parseHsl(css: string): HSL | null {
  const m = css.match(/hsl\(\s*([\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%/i);
  return m ? { h: Number(m[1]), s: Number(m[2]) / 100, l: Number(m[3]) / 100 } : null;
}

/**
 * How deep a card ground goes. A page ground is pale by law (L 88%); at that
 * lightness a 1200×630 card in a timeline reads as a blank rectangle, and the
 * ink on it has nowhere to go. 24% is the shallowest depth at which the house
 * light ink clears WCAG AA for large type on every hue on the wheel, so it is
 * the most of the page's own paleness a card can keep.
 */
export const CARD_DEPTH = 0.24;

/** the least saturated a deepened ground may be, so depth never costs colour */
const CARD_SAT = { min: 0.34, max: 0.78 } as const;

export interface PortalPalette {
  /** the page's ground, exactly as the page paints it */
  pageGround: string;
  /** the page's creature */
  state: ColorState;
  /** the creature's hue — which baked bloom the card plants */
  nettiHue: number;
  /** whatever identifies the thing pictured, for the non-colour choices */
  seed: string;
}

/**
 * The card for a page: its ground at reading depth, its creature, its glow.
 */
export function portalPalette({ pageGround, state, nettiHue, seed }: PortalPalette): ThumbPalette {
  const rnd = rngFrom(seedFromString(seed));
  const p = derivePalette(state);
  const hsl = parseHsl(pageGround);

  // the page's own hue, taken down to card depth. A ground that is ALREADY
  // deep (the night rooms — about, team) is left where it is: the card is then
  // literally the page's background, which is the whole point.
  let ground: RGB;
  if (hsl) {
    ground = hslToRgb({
      h: hsl.h,
      s: Math.min(CARD_SAT.max, Math.max(CARD_SAT.min, hsl.s)),
      l: Math.min(hsl.l, CARD_DEPTH),
    });
  } else {
    // an unparseable ground (a gradient, a hex) still has to render something
    ground = saturate(shade(p.core, 0.38), 1.3);
  }

  let ink = contrastRatio(INK_LIGHT, ground) >= contrastRatio(INK_DARK, ground) ? INK_LIGHT : INK_DARK;
  for (let i = 0; i < 5 && contrastRatio(ink, ground) < 4.5; i++) {
    ground = saturate(shade(ground, 0.12), 1.18);
    ink = contrastRatio(INK_LIGHT, ground) >= contrastRatio(INK_DARK, ground) ? INK_LIGHT : INK_DARK;
  }

  const groundCss = rgbToCss(ground);
  const inkCss = ink === INK_LIGHT ? 'rgba(255, 252, 246, 0.97)' : 'rgba(43, 38, 34, 0.94)';

  return {
    state,
    hue: representativeOrganismHue(state),
    // THE PAGE'S OWN CREATURE — never the one furthest from the ground. On a
    // card built from a hash that contrast was earned; here it would put a
    // different netti on the card than the one standing on the page.
    nettiSrc: nettiForHue(nettiHue),
    nettiHue,
    ground: groundCss,
    groundFrom: groundCss,
    groundTo: groundCss,
    angle: Math.round(118 + rnd() * 74),
    glow: rgba(tint(p.glow, 0.18), 0.58),
    glowSoft: rgba(tint(p.halo, 0.3), 0.34),
    fringe: rgba(tint(p.fringe, 0.12), 0.3),
    glowAt: { x: Math.round(58 + rnd() * 22), y: Math.round(32 + rnd() * 26) },
    ink: inkCss,
    inkSoft: ink === INK_LIGHT ? rgba(tint(p.halo, 0.55), 0.82) : 'rgba(43, 38, 34, 0.6)',
    accent: rgbToCss(hslToRgb({ h: nettiHue, s: 0.86, l: 0.68 })),
    tilt: Math.round(rnd() * 22 - 11),
  };
}

/** The CSS custom properties the `.dthumb` markup consumes. */
export function thumbVars(seed: string): Record<string, string> {
  const t = thumbPalette(seed);
  return {
    '--dt-from': t.groundFrom,
    '--dt-to': t.groundTo,
    '--dt-angle': `${t.angle}deg`,
    '--dt-glow': t.glow,
    '--dt-glow-soft': t.glowSoft,
    '--dt-fringe': t.fringe,
    '--dt-glow-x': `${t.glowAt.x}%`,
    '--dt-glow-y': `${t.glowAt.y}%`,
    '--dt-ink': t.ink,
    '--dt-ink-soft': t.inkSoft,
    '--dt-tilt': `${t.tilt}deg`,
    '--dt-netti': `url(${t.nettiSrc})`,
  };
}
